import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { LockIcon, ArrowLeftIcon } from "lucide-react";
import { z } from "zod";

import { cn } from "./lib/cn";
import { AuthHttpError } from "./auth-http-error";
import { authResetPassword } from "./auth-fetch";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";

export type ResetPasswordFormMessages = {
  errors: {
    passwordMinLength: string;
    passwordMaxLength: string;
    passwordsDoNotMatch: string;
    invalidResetToken: string;
    invalidResetTokenDesc: string;
    resetPasswordFailed: string;
    resetPasswordFailedDesc: string;
  };
  resetPassword: {
    title: string;
    subtitle: string;
    newPassword: string;
    confirmPassword: string;
    submitButton: string;
    backToLogin: string;
    success: string;
    successDesc: string;
  };
};

type ResetPasswordFormProps = {
  authBaseUrl: string;
  token: string;
  messages: ResetPasswordFormMessages;
  heroImageSrc?: string;
  onNavigateToLogin: () => void;
  onSuccess?: () => void;
  footerSlot?: React.ReactNode;
  className?: string;
};

export function ResetPasswordForm({
  authBaseUrl,
  token,
  messages: m,
  heroImageSrc,
  onNavigateToLogin,
  onSuccess,
  footerSlot,
  className,
  ...props
}: ResetPasswordFormProps &
  Omit<React.ComponentProps<"div">, keyof ResetPasswordFormProps>) {
  const formSchema = z
    .object({
      password: z
        .string()
        .min(8, { message: m.errors.passwordMinLength })
        .max(50, { message: m.errors.passwordMaxLength }),
      confirmPassword: z
        .string()
        .min(8, { message: m.errors.passwordMinLength })
        .max(50, { message: m.errors.passwordMaxLength }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: m.errors.passwordsDoNotMatch,
      path: ["confirmPassword"],
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: { password: "", confirmPassword: "" },
  });

  const mutation = useMutation({
    mutationFn: ({ password }: { password: string }) =>
      authResetPassword(authBaseUrl, { nonce: token, newPassword: password }),
    onError: (error: unknown) => {
      if (error instanceof AuthHttpError && error.status === 400) {
        toast.error(m.errors.invalidResetToken, {
          icon: <LockIcon />,
          description: m.errors.invalidResetTokenDesc,
          richColors: true,
        });
      } else {
        toast.error(m.errors.resetPasswordFailed, {
          description: m.errors.resetPasswordFailedDesc,
          richColors: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(m.resetPassword.success, {
        description: m.resetPassword.successDesc,
        richColors: true,
      });
      onSuccess?.();
    },
  });

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Form {...form}>
            <form
              className="p-6 md:p-8"
              onSubmit={form.handleSubmit((values) =>
                mutation.mutate({ password: values.password }),
              )}
            >
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold">
                    {m.resetPassword.title}
                  </h1>
                  <p className="text-muted-foreground text-balance">
                    {m.resetPassword.subtitle}
                  </p>
                </div>
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{m.resetPassword.newPassword}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            required
                            autoComplete="new-password"
                            tabIndex={1}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {m.resetPassword.confirmPassword}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            required
                            autoComplete="new-password"
                            tabIndex={2}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button type="submit" className="w-full" tabIndex={3}>
                  {m.resetPassword.submitButton}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={onNavigateToLogin}
                  tabIndex={4}
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  {m.resetPassword.backToLogin}
                </Button>
                {footerSlot ? (
                  <div className="flex justify-center pt-2">{footerSlot}</div>
                ) : null}
              </div>
            </form>
          </Form>
          {heroImageSrc ? (
            <div className="bg-muted relative hidden md:block">
              <img
                src={heroImageSrc}
                alt=""
                className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
